import React, { useState, useEffect, useMemo } from 'react';

const INVENTORY_STORAGE_KEY = 'blissMaterialInventory';

// Sheet goods sizes
const SHEET_SIZES = [
  { value: '4x8', label: '4\' × 8\' (Standard)', length: 96, width: 48 },
  { value: '4x4', label: '4\' × 4\' (Half Sheet)', length: 48, width: 48 },
  { value: '2x4', label: '2\' × 4\' (Quarter Sheet)', length: 48, width: 24 },
  { value: '5x5', label: '5\' × 5\'', length: 60, width: 60 },
  { value: 'custom', label: 'Custom Size', length: 0, width: 0 },
];

// Common sheet thicknesses (fractional)
const SHEET_THICKNESSES = [
  { value: 0.25, label: '1/4"', decimal: '0.25' },
  { value: 0.375, label: '3/8"', decimal: '0.375' },
  { value: 0.46875, label: '15/32"', decimal: '0.469' },
  { value: 0.5, label: '1/2"', decimal: '0.5' },
  { value: 0.59375, label: '19/32"', decimal: '0.594' },
  { value: 0.625, label: '5/8"', decimal: '0.625' },
  { value: 0.6875, label: '11/16"', decimal: '0.688' },
  { value: 0.71875, label: '23/32"', decimal: '0.719' },
  { value: 0.75, label: '3/4"', decimal: '0.75' },
  { value: 1.0, label: '1"', decimal: '1.0' },
  { value: 1.5, label: '1-1/2"', decimal: '1.5' },
];

// Sheet goods types
const SHEET_TYPES = [
  { value: 'plywood', label: 'Plywood' },
  { value: 'mdf', label: 'MDF' },
  { value: 'particleboard', label: 'Particleboard' },
  { value: 'melamine', label: 'Melamine' },
  { value: 'hardboard', label: 'Hardboard' },
  { value: 'osb', label: 'OSB' },
];

// Wood species options
const WOOD_SPECIES = [
  { value: 'pine', label: 'Pine' },
  { value: 'poplar', label: 'Poplar' },
  { value: 'oak', label: 'Oak' },
  { value: 'maple', label: 'Maple' },
  { value: 'walnut', label: 'Walnut' },
  { value: 'birch', label: 'Birch' },
  { value: 'cherry', label: 'Cherry' },
  { value: 'alder', label: 'Alder' },
  { value: 'ash', label: 'Ash' },
  { value: 'cedar', label: 'Cedar' },
  { value: 'baltic_birch', label: 'Baltic Birch' },
  { value: 'mdf', label: 'MDF (No Species)' },
  { value: 'white', label: 'White (Melamine)' },
  { value: 'other', label: 'Other' },
];

// Lumber dimensions with actual sizes
const LUMBER_DIMENSIONS = [
  { value: '1x2', label: '1×2', actual: '3/4" × 1-1/2"' },
  { value: '1x3', label: '1×3', actual: '3/4" × 2-1/2"' },
  { value: '1x4', label: '1×4', actual: '3/4" × 3-1/2"' },
  { value: '1x6', label: '1×6', actual: '3/4" × 5-1/2"' },
  { value: '1x8', label: '1×8', actual: '3/4" × 7-1/4"' },
  { value: '1x10', label: '1×10', actual: '3/4" × 9-1/4"' },
  { value: '1x12', label: '1×12', actual: '3/4" × 11-1/4"' },
  { value: '2x2', label: '2×2', actual: '1-1/2" × 1-1/2"' },
  { value: '2x3', label: '2×3', actual: '1-1/2" × 2-1/2"' },
  { value: '2x4', label: '2×4', actual: '1-1/2" × 3-1/2"' },
  { value: '2x6', label: '2×6', actual: '1-1/2" × 5-1/2"' },
  { value: '2x8', label: '2×8', actual: '1-1/2" × 7-1/4"' },
  { value: '2x10', label: '2×10', actual: '1-1/2" × 9-1/4"' },
  { value: '2x12', label: '2×12', actual: '1-1/2" × 11-1/4"' },
  { value: '4x4', label: '4×4', actual: '3-1/2" × 3-1/2"' },
  { value: '4x6', label: '4×6', actual: '3-1/2" × 5-1/2"' },
  { value: '6x6', label: '6×6', actual: '5-1/2" × 5-1/2"' },
];

// Standard lumber lengths
const LUMBER_LENGTHS = [
  { value: 48, label: '4 ft (48")' },
  { value: 72, label: '6 ft (72")' },
  { value: 96, label: '8 ft (96")' },
  { value: 120, label: '10 ft (120")' },
  { value: 144, label: '12 ft (144")' },
  { value: 168, label: '14 ft (168")' },
  { value: 192, label: '16 ft (192")' },
  { value: 240, label: '20 ft (240")' },
];

function getInventory() {
  try {
    const stored = localStorage.getItem(INVENTORY_STORAGE_KEY);
    if (!stored) return { sheets: [], lumber: [] };
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading inventory:', error);
    return { sheets: [], lumber: [] };
  }
}

function saveInventory(inventory) {
  try {
    localStorage.setItem(INVENTORY_STORAGE_KEY, JSON.stringify(inventory));
  } catch (error) {
    console.error('Error saving inventory:', error);
  }
}

export default function Inventory() {
  const [inventory, setInventory] = useState({ sheets: [], lumber: [] });
  const [activeTab, setActiveTab] = useState('sheets'); // 'sheets' or 'lumber'
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSpecies, setFilterSpecies] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);

  // Sheet form state
  const [sheetForm, setSheetForm] = useState({
    type: 'plywood',
    species: 'pine',
    size: '4x8',
    customLength: '',
    customWidth: '',
    thickness: 0.75,
    quantity: 1,
    costPerSheet: '',
    reorderPoint: 2,
    location: '',
    supplier: '',
    notes: '',
  });

  // Lumber form state
  const [lumberForm, setLumberForm] = useState({
    dimension: '2x4',
    species: 'pine',
    length: 96,
    quantity: 1,
    costPerPiece: '',
    reorderPoint: 5,
    location: '',
    supplier: '',
    notes: '',
  });

  useEffect(() => {
    setInventory(getInventory());
  }, []);

  const generateId = () => {
    return `INV-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`.toUpperCase();
  };

  // Format thickness to fractional
  const formatThickness = (thickness) => {
    const match = SHEET_THICKNESSES.find(t => Math.abs(t.value - thickness) < 0.01);
    if (match) return match.label;
    return `${thickness}"`;
  };

  // Handle sheet form submission
  const handleSheetSubmit = (e) => {
    e.preventDefault();
    
    const sizeInfo = SHEET_SIZES.find(s => s.value === sheetForm.size);
    const newSheet = {
      id: editingItem?.id || generateId(),
      type: sheetForm.type,
      species: sheetForm.species,
      size: sheetForm.size,
      length: sheetForm.size === 'custom' ? parseFloat(sheetForm.customLength) : sizeInfo.length,
      width: sheetForm.size === 'custom' ? parseFloat(sheetForm.customWidth) : sizeInfo.width,
      thickness: sheetForm.thickness,
      quantity: parseInt(sheetForm.quantity) || 0,
      costPerSheet: parseFloat(sheetForm.costPerSheet) || 0,
      reorderPoint: parseInt(sheetForm.reorderPoint) || 0,
      location: sheetForm.location,
      supplier: sheetForm.supplier,
      notes: sheetForm.notes,
      dateAdded: editingItem?.dateAdded || new Date().toISOString(),
      dateModified: new Date().toISOString(),
    };

    let updatedSheets;
    if (editingItem) {
      updatedSheets = inventory.sheets.map(item => 
        item.id === editingItem.id ? newSheet : item
      );
    } else {
      updatedSheets = [...inventory.sheets, newSheet];
    }

    const updatedInventory = { ...inventory, sheets: updatedSheets };
    setInventory(updatedInventory);
    saveInventory(updatedInventory);
    resetForm();
  };

  // Handle lumber form submission
  const handleLumberSubmit = (e) => {
    e.preventDefault();
    
    const newLumber = {
      id: editingItem?.id || generateId(),
      dimension: lumberForm.dimension,
      species: lumberForm.species,
      length: parseInt(lumberForm.length) || 96,
      quantity: parseInt(lumberForm.quantity) || 0,
      costPerPiece: parseFloat(lumberForm.costPerPiece) || 0,
      reorderPoint: parseInt(lumberForm.reorderPoint) || 0,
      location: lumberForm.location,
      supplier: lumberForm.supplier,
      notes: lumberForm.notes,
      dateAdded: editingItem?.dateAdded || new Date().toISOString(),
      dateModified: new Date().toISOString(),
    };

    let updatedLumber;
    if (editingItem) {
      updatedLumber = inventory.lumber.map(item => 
        item.id === editingItem.id ? newLumber : item
      );
    } else {
      updatedLumber = [...inventory.lumber, newLumber];
    }

    const updatedInventory = { ...inventory, lumber: updatedLumber };
    setInventory(updatedInventory);
    saveInventory(updatedInventory);
    resetForm();
  };

  const resetForm = () => {
    setSheetForm({
      type: 'plywood',
      species: 'pine',
      size: '4x8',
      customLength: '',
      customWidth: '',
      thickness: 0.75,
      quantity: 1,
      costPerSheet: '',
      reorderPoint: 2,
      location: '',
      supplier: '',
      notes: '',
    });
    setLumberForm({
      dimension: '2x4',
      species: 'pine',
      length: 96,
      quantity: 1,
      costPerPiece: '',
      reorderPoint: 5,
      location: '',
      supplier: '',
      notes: '',
    });
    setShowAddForm(false);
    setEditingItem(null);
  };

  const handleEditSheet = (item) => {
    setActiveTab('sheets');
    setEditingItem(item);
    const closestThickness = SHEET_THICKNESSES.reduce((prev, curr) => 
      Math.abs(curr.value - item.thickness) < Math.abs(prev.value - item.thickness) ? curr : prev
    ).value;
    
    setSheetForm({
      type: item.type || 'plywood',
      species: item.species || 'pine',
      size: item.size || '4x8',
      customLength: item.length?.toString() || '',
      customWidth: item.width?.toString() || '',
      thickness: closestThickness,
      quantity: item.quantity?.toString() || '1',
      costPerSheet: item.costPerSheet?.toString() || '',
      reorderPoint: item.reorderPoint?.toString() || '2',
      location: item.location || '',
      supplier: item.supplier || '',
      notes: item.notes || '',
    });
    setShowAddForm(true);
  };

  const handleEditLumber = (item) => {
    setActiveTab('lumber');
    setEditingItem(item);
    setLumberForm({
      dimension: item.dimension || '2x4',
      species: item.species || 'pine',
      length: item.length || 96,
      quantity: item.quantity?.toString() || '1',
      costPerPiece: item.costPerPiece?.toString() || '',
      reorderPoint: item.reorderPoint?.toString() || '5',
      location: item.location || '',
      supplier: item.supplier || '',
      notes: item.notes || '',
    });
    setShowAddForm(true);
  };

  const handleDeleteSheet = (id) => {
    if (window.confirm('Are you sure you want to delete this sheet goods item?')) {
      const updatedSheets = inventory.sheets.filter(item => item.id !== id);
      const updatedInventory = { ...inventory, sheets: updatedSheets };
      setInventory(updatedInventory);
      saveInventory(updatedInventory);
    }
  };

  const handleDeleteLumber = (id) => {
    if (window.confirm('Are you sure you want to delete this lumber item?')) {
      const updatedLumber = inventory.lumber.filter(item => item.id !== id);
      const updatedInventory = { ...inventory, lumber: updatedLumber };
      setInventory(updatedInventory);
      saveInventory(updatedInventory);
    }
  };

  const handleAdjustQuantity = (type, id, adjustment) => {
    if (type === 'sheets') {
      const updatedSheets = inventory.sheets.map(item => {
        if (item.id === id) {
          const newQty = Math.max(0, (item.quantity || 0) + adjustment);
          return { ...item, quantity: newQty, dateModified: new Date().toISOString() };
        }
        return item;
      });
      const updatedInventory = { ...inventory, sheets: updatedSheets };
      setInventory(updatedInventory);
      saveInventory(updatedInventory);
    } else {
      const updatedLumber = inventory.lumber.map(item => {
        if (item.id === id) {
          const newQty = Math.max(0, (item.quantity || 0) + adjustment);
          return { ...item, quantity: newQty, dateModified: new Date().toISOString() };
        }
        return item;
      });
      const updatedInventory = { ...inventory, lumber: updatedLumber };
      setInventory(updatedInventory);
      saveInventory(updatedInventory);
    }
  };

  // Filter sheets
  const filteredSheets = useMemo(() => {
    let result = [...(inventory.sheets || [])];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.type?.toLowerCase().includes(term) ||
        item.species?.toLowerCase().includes(term) ||
        item.location?.toLowerCase().includes(term) ||
        item.supplier?.toLowerCase().includes(term)
      );
    }

    if (filterType !== 'all') {
      result = result.filter(item => item.type === filterType);
    }

    if (filterSpecies !== 'all') {
      result = result.filter(item => item.species === filterSpecies);
    }

    if (showLowStock) {
      result = result.filter(item => item.quantity <= item.reorderPoint);
    }

    return result;
  }, [inventory.sheets, searchTerm, filterType, filterSpecies, showLowStock]);

  // Filter lumber
  const filteredLumber = useMemo(() => {
    let result = [...(inventory.lumber || [])];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.dimension?.toLowerCase().includes(term) ||
        item.species?.toLowerCase().includes(term) ||
        item.location?.toLowerCase().includes(term) ||
        item.supplier?.toLowerCase().includes(term)
      );
    }

    if (filterSpecies !== 'all') {
      result = result.filter(item => item.species === filterSpecies);
    }

    if (showLowStock) {
      result = result.filter(item => item.quantity <= item.reorderPoint);
    }

    return result;
  }, [inventory.lumber, searchTerm, filterSpecies, showLowStock]);

  // Summary stats
  const summary = useMemo(() => {
    const sheets = inventory.sheets || [];
    const lumber = inventory.lumber || [];
    
    const totalSheets = sheets.reduce((sum, s) => sum + (s.quantity || 0), 0);
    const totalLumber = lumber.reduce((sum, l) => sum + (l.quantity || 0), 0);
    const lowStockSheets = sheets.filter(s => s.quantity <= s.reorderPoint).length;
    const lowStockLumber = lumber.filter(l => l.quantity <= l.reorderPoint).length;
    const totalValue = 
      sheets.reduce((sum, s) => sum + ((s.quantity || 0) * (s.costPerSheet || 0)), 0) +
      lumber.reduce((sum, l) => sum + ((l.quantity || 0) * (l.costPerPiece || 0)), 0);

    return {
      totalSheets,
      totalLumber,
      lowStockSheets,
      lowStockLumber,
      totalLowStock: lowStockSheets + lowStockLumber,
      totalValue,
      sheetTypes: sheets.length,
      lumberTypes: lumber.length,
    };
  }, [inventory]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-light text-white mb-2">
              <span className="text-warm-gold">📦</span> Material Inventory
            </h1>
            <p className="text-gray-custom text-sm">
              Track plywood sheets, lumber, and other materials in stock
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-gold px-6 py-3 text-sm flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Material
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-warm-gold mb-1">{summary.totalSheets}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Sheet Goods</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-blue-400 mb-1">{summary.totalLumber}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Lumber Pcs</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-green-400 mb-1">{summary.sheetTypes}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Sheet Types</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-3xl font-light text-purple-400 mb-1">{summary.lumberTypes}</div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Lumber Types</div>
        </div>
        <div className={`card p-4 text-center ${summary.totalLowStock > 0 ? 'border-red-500/50' : ''}`}>
          <div className={`text-3xl font-light mb-1 ${summary.totalLowStock > 0 ? 'text-red-400' : 'text-gray-light'}`}>
            {summary.totalLowStock}
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Low Stock</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-light text-emerald-400 mb-1">
            ${summary.totalValue.toLocaleString()}
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-custom">Total Value</div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('sheets')}
          className={`flex-1 py-4 rounded-lg text-center font-medium transition ${
            activeTab === 'sheets'
              ? 'bg-warm-gold text-deep-navy'
              : 'bg-charcoal-light text-gray-light hover:bg-charcoal border border-white/10'
          }`}
        >
          🪵 Sheet Goods ({inventory.sheets?.length || 0})
          {summary.lowStockSheets > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">
              {summary.lowStockSheets} low
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('lumber')}
          className={`flex-1 py-4 rounded-lg text-center font-medium transition ${
            activeTab === 'lumber'
              ? 'bg-warm-gold text-deep-navy'
              : 'bg-charcoal-light text-gray-light hover:bg-charcoal border border-white/10'
          }`}
        >
          🪓 Lumber ({inventory.lumber?.length || 0})
          {summary.lowStockLumber > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">
              {summary.lowStockLumber} low
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Search inventory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-styled"
            />
          </div>
          
          {activeTab === 'sheets' && (
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="select-styled"
            >
              <option value="all">All Sheet Types</option>
              {SHEET_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          )}
          
          <select
            value={filterSpecies}
            onChange={(e) => setFilterSpecies(e.target.value)}
            className="select-styled"
          >
            <option value="all">All Species</option>
            {WOOD_SPECIES.map(species => (
              <option key={species.value} value={species.value}>{species.label}</option>
            ))}
          </select>
          
          <button
            onClick={() => setShowLowStock(!showLowStock)}
            className={`px-4 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
              showLowStock
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : 'bg-charcoal-light text-gray-light border border-white/10 hover:border-white/20'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Low Stock Only
          </button>
        </div>
      </div>

      {/* Sheet Goods Table */}
      {activeTab === 'sheets' && (
        <div className="card p-6 overflow-x-auto">
          {filteredSheets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🪵</div>
              <p className="text-gray-custom text-lg mb-2">No sheet goods found</p>
              <p className="text-gray-custom text-sm mb-6">
                {inventory.sheets?.length === 0 
                  ? "Add your first sheet goods to track inventory"
                  : "Try adjusting your filters"}
              </p>
              {inventory.sheets?.length === 0 && (
                <button
                  onClick={() => { setActiveTab('sheets'); setShowAddForm(true); }}
                  className="btn-gold-outline px-6 py-2 text-sm"
                >
                  Add First Sheet
                </button>
              )}
            </div>
          ) : (
            <table className="table-styled">
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Size</th>
                  <th>Thickness</th>
                  <th className="text-center">Qty</th>
                  <th>Cost</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSheets.map((item) => {
                  const isLowStock = item.quantity <= item.reorderPoint;
                  return (
                    <tr key={item.id} className={isLowStock ? 'bg-red-500/5' : ''}>
                      <td>
                        <div className="font-medium text-white">
                          {WOOD_SPECIES.find(s => s.value === item.species)?.label || item.species}
                        </div>
                        <div className="text-xs text-gray-custom">
                          {SHEET_TYPES.find(t => t.value === item.type)?.label || item.type}
                        </div>
                      </td>
                      <td className="text-gray-light">
                        {item.size === 'custom' 
                          ? `${item.length}" × ${item.width}"`
                          : SHEET_SIZES.find(s => s.value === item.size)?.label || item.size}
                      </td>
                      <td className="text-gray-light">{formatThickness(item.thickness)}</td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleAdjustQuantity('sheets', item.id, -1)}
                            className="w-7 h-7 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition"
                          >
                            −
                          </button>
                          <span className={`font-bold text-lg min-w-[40px] ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleAdjustQuantity('sheets', item.id, 1)}
                            className="w-7 h-7 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 transition"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="text-gray-light">
                        {item.costPerSheet ? `$${item.costPerSheet.toFixed(2)}` : '—'}
                      </td>
                      <td className="text-gray-light">{item.location || '—'}</td>
                      <td>
                        {isLowStock ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                            ⚠️ Low Stock
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEditSheet(item)}
                            className="p-2 text-gray-light hover:text-warm-gold transition"
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteSheet(item.id)}
                            className="p-2 text-gray-light hover:text-red-400 transition"
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Lumber Table */}
      {activeTab === 'lumber' && (
        <div className="card p-6 overflow-x-auto">
          {filteredLumber.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">🪓</div>
              <p className="text-gray-custom text-lg mb-2">No lumber found</p>
              <p className="text-gray-custom text-sm mb-6">
                {inventory.lumber?.length === 0 
                  ? "Add your first lumber to track inventory"
                  : "Try adjusting your filters"}
              </p>
              {inventory.lumber?.length === 0 && (
                <button
                  onClick={() => { setActiveTab('lumber'); setShowAddForm(true); }}
                  className="btn-gold-outline px-6 py-2 text-sm"
                >
                  Add First Lumber
                </button>
              )}
            </div>
          ) : (
            <table className="table-styled">
              <thead>
                <tr>
                  <th>Dimension</th>
                  <th>Species</th>
                  <th>Length</th>
                  <th className="text-center">Qty</th>
                  <th>Cost</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLumber.map((item) => {
                  const isLowStock = item.quantity <= item.reorderPoint;
                  const dimInfo = LUMBER_DIMENSIONS.find(d => d.value === item.dimension);
                  return (
                    <tr key={item.id} className={isLowStock ? 'bg-red-500/5' : ''}>
                      <td>
                        <div className="font-medium text-white">{item.dimension}</div>
                        <div className="text-xs text-gray-custom">
                          {dimInfo?.actual || ''}
                        </div>
                      </td>
                      <td className="text-gray-light">
                        {WOOD_SPECIES.find(s => s.value === item.species)?.label || item.species}
                      </td>
                      <td className="text-gray-light">
                        {item.length}" ({Math.floor(item.length / 12)} ft)
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleAdjustQuantity('lumber', item.id, -1)}
                            className="w-7 h-7 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition"
                          >
                            −
                          </button>
                          <span className={`font-bold text-lg min-w-[40px] ${isLowStock ? 'text-red-400' : 'text-white'}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => handleAdjustQuantity('lumber', item.id, 1)}
                            className="w-7 h-7 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30 transition"
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="text-gray-light">
                        {item.costPerPiece ? `$${item.costPerPiece.toFixed(2)}` : '—'}
                      </td>
                      <td className="text-gray-light">{item.location || '—'}</td>
                      <td>
                        {isLowStock ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                            ⚠️ Low Stock
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEditLumber(item)}
                            className="p-2 text-gray-light hover:text-warm-gold transition"
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteLumber(item.id)}
                            className="p-2 text-gray-light hover:text-red-400 transition"
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-charcoal rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-warm-gold/30">
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-warm-gold text-xs uppercase tracking-wider mb-1">
                    {editingItem ? 'Edit Inventory' : 'Add New Material'}
                  </p>
                  <h2 className="text-xl font-light text-white">
                    {activeTab === 'sheets' ? 'Sheet Goods' : 'Lumber'}
                  </h2>
                </div>
                <button
                  onClick={resetForm}
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Type Toggle (only when adding new) */}
              {!editingItem && (
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setActiveTab('sheets')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'sheets'
                        ? 'bg-warm-gold text-deep-navy'
                        : 'bg-charcoal-light text-gray-light'
                    }`}
                  >
                    Sheet Goods
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('lumber')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'lumber'
                        ? 'bg-warm-gold text-deep-navy'
                        : 'bg-charcoal-light text-gray-light'
                    }`}
                  >
                    Lumber
                  </button>
                </div>
              )}
            </div>

            {/* Sheet Goods Form */}
            {activeTab === 'sheets' && (
              <form onSubmit={handleSheetSubmit} className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Sheet Type *</label>
                    <select
                      value={sheetForm.type}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, type: e.target.value }))}
                      className="select-styled"
                      required
                    >
                      {SHEET_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="info-label mb-2">Wood Species *</label>
                    <select
                      value={sheetForm.species}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, species: e.target.value }))}
                      className="select-styled"
                      required
                    >
                      {WOOD_SPECIES.map(species => (
                        <option key={species.value} value={species.value}>{species.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-sm uppercase tracking-wider text-warm-gold mb-3">Dimensions</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="info-label mb-2">Sheet Size *</label>
                      <select
                        value={sheetForm.size}
                        onChange={(e) => setSheetForm(prev => ({ ...prev, size: e.target.value }))}
                        className="select-styled"
                        required
                      >
                        {SHEET_SIZES.map(size => (
                          <option key={size.value} value={size.value}>{size.label}</option>
                        ))}
                      </select>
                    </div>
                    {sheetForm.size === 'custom' && (
                      <>
                        <div>
                          <label className="info-label mb-2">Length (in) *</label>
                          <input
                            type="number"
                            value={sheetForm.customLength}
                            onChange={(e) => setSheetForm(prev => ({ ...prev, customLength: e.target.value }))}
                            className="input-styled"
                            placeholder="96"
                            required
                          />
                        </div>
                        <div>
                          <label className="info-label mb-2">Width (in) *</label>
                          <input
                            type="number"
                            value={sheetForm.customWidth}
                            onChange={(e) => setSheetForm(prev => ({ ...prev, customWidth: e.target.value }))}
                            className="input-styled"
                            placeholder="48"
                            required
                          />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="info-label mb-2">Thickness *</label>
                      <select
                        value={sheetForm.thickness}
                        onChange={(e) => setSheetForm(prev => ({ ...prev, thickness: parseFloat(e.target.value) }))}
                        className="select-styled"
                        required
                      >
                        {SHEET_THICKNESSES.map(t => (
                          <option key={t.value} value={t.value}>
                            {t.label} ({t.decimal}")
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Quantity *</label>
                    <input
                      type="number"
                      value={sheetForm.quantity}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, quantity: e.target.value }))}
                      className="input-styled"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Cost per Sheet</label>
                    <input
                      type="number"
                      value={sheetForm.costPerSheet}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, costPerSheet: e.target.value }))}
                      className="input-styled"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Reorder Point</label>
                    <input
                      type="number"
                      value={sheetForm.reorderPoint}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, reorderPoint: e.target.value }))}
                      className="input-styled"
                      min="0"
                    />
                    <p className="text-xs text-gray-custom mt-1">Alert when stock falls to this level</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Storage Location</label>
                    <input
                      type="text"
                      value={sheetForm.location}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, location: e.target.value }))}
                      className="input-styled"
                      placeholder="e.g., Rack A, Bay 2"
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Supplier</label>
                    <input
                      type="text"
                      value={sheetForm.supplier}
                      onChange={(e) => setSheetForm(prev => ({ ...prev, supplier: e.target.value }))}
                      className="input-styled"
                      placeholder="e.g., Home Depot"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="info-label mb-2">Notes</label>
                  <textarea
                    value={sheetForm.notes}
                    onChange={(e) => setSheetForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="input-styled min-h-[80px] resize-y"
                    placeholder="Any additional notes..."
                  />
                </div>

                <div className="flex gap-3">
                  <button type="submit" className="btn-gold flex-1 py-3">
                    {editingItem ? 'Update Sheet' : 'Add to Inventory'}
                  </button>
                  <button type="button" onClick={resetForm} className="btn-gold-outline px-6 py-3">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Lumber Form */}
            {activeTab === 'lumber' && (
              <form onSubmit={handleLumberSubmit} className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Lumber Dimension *</label>
                    <select
                      value={lumberForm.dimension}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, dimension: e.target.value }))}
                      className="select-styled"
                      required
                    >
                      {LUMBER_DIMENSIONS.map(dim => (
                        <option key={dim.value} value={dim.value}>
                          {dim.label} (actual: {dim.actual})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="info-label mb-2">Wood Species *</label>
                    <select
                      value={lumberForm.species}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, species: e.target.value }))}
                      className="select-styled"
                      required
                    >
                      {WOOD_SPECIES.map(species => (
                        <option key={species.value} value={species.value}>{species.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="info-label mb-2">Length *</label>
                  <select
                    value={lumberForm.length}
                    onChange={(e) => setLumberForm(prev => ({ ...prev, length: parseInt(e.target.value) }))}
                    className="select-styled"
                    required
                  >
                    {LUMBER_LENGTHS.map(len => (
                      <option key={len.value} value={len.value}>{len.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Quantity *</label>
                    <input
                      type="number"
                      value={lumberForm.quantity}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, quantity: e.target.value }))}
                      className="input-styled"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Cost per Piece</label>
                    <input
                      type="number"
                      value={lumberForm.costPerPiece}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, costPerPiece: e.target.value }))}
                      className="input-styled"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Reorder Point</label>
                    <input
                      type="number"
                      value={lumberForm.reorderPoint}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, reorderPoint: e.target.value }))}
                      className="input-styled"
                      min="0"
                    />
                    <p className="text-xs text-gray-custom mt-1">Alert when stock falls to this level</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="info-label mb-2">Storage Location</label>
                    <input
                      type="text"
                      value={lumberForm.location}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, location: e.target.value }))}
                      className="input-styled"
                      placeholder="e.g., Lumber Rack B"
                    />
                  </div>
                  <div>
                    <label className="info-label mb-2">Supplier</label>
                    <input
                      type="text"
                      value={lumberForm.supplier}
                      onChange={(e) => setLumberForm(prev => ({ ...prev, supplier: e.target.value }))}
                      className="input-styled"
                      placeholder="e.g., Lumber Yard"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="info-label mb-2">Notes</label>
                  <textarea
                    value={lumberForm.notes}
                    onChange={(e) => setLumberForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="input-styled min-h-[80px] resize-y"
                    placeholder="Any additional notes..."
                  />
                </div>

                <div className="flex gap-3">
                  <button type="submit" className="btn-gold flex-1 py-3">
                    {editingItem ? 'Update Lumber' : 'Add to Inventory'}
                  </button>
                  <button type="button" onClick={resetForm} className="btn-gold-outline px-6 py-3">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
